import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { mockProducts } from "../mock";
import { ArrowRight, Download, CheckCircle } from "lucide-react";

const ProductsPage = () => {
  const { t } = useLanguage();

  const productFeatures = {
    1: ["Optimisation de l'espace de travail", "Déplacement de charges lourdes", "Structure aérienne robuste", "Installation sur rails"],
    2: ["Structures robustes", "Mobilité sur pneus ou rails", "Levage et déplacement", "Environnements industriels"],
    3: ["Systèmes pivotants", "Postes de travail individuels", "Espaces limités", "Installation murale ou sur poteau"],
    4: ["Manipulation précise", "Ergonomie renforcée", "Sécurité opérateur", "Solutions pneumatiques/électriques"],
    5: ["Inspection certifiée", "Maintenance préventive", "Réparations d'urgence", "Modernisation d'équipements"]
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-excel-steel to-excel-dark py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t("products")} & Services
            </h1>
            <p className="text-xl max-w-3xl mx-auto leading-relaxed">
              Excel Levage conçoit et installe des solutions adaptées à vos environnements de production et de manutention lourde.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {mockProducts.map((product, index) => (
              <div key={product.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="space-y-4">
                    <Badge className="bg-excel-light-red text-white">
                      Expertise Excel Levage
                    </Badge>
                    
                    <h2 className="text-3xl md:text-4xl font-bold text-excel-dark">
                      {t(product.name)}
                    </h2>
                    
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {t(product.description)}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="space-y-3">
                    {productFeatures[product.id]?.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-excel-light-red flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Capacities */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-excel-dark">Capacités disponibles :</h3>
                    <div className="flex flex-wrap gap-2">
                      {product.capacities.map((capacity, idx) => (
                        <Badge key={idx} variant="outline" className="border-excel-steel text-excel-steel">
                          {capacity}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button className="bg-excel-light-red hover:bg-excel-red text-white">
                      <Link to="/contact" className="flex items-center">
                        Demander un devis
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="outline" className="border-excel-steel text-excel-steel hover:bg-excel-steel hover:text-white">
                      <Download className="mr-2 h-4 w-4" />
                      Télécharger la fiche
                    </Button>
                  </div>
                </div>

                <div className={`relative ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <img
                    src={product.image}
                    alt={t(product.name)}
                    className="w-full h-96 object-cover rounded-lg shadow-lg"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-excel-dark mb-4">
              Spécifications techniques
            </h2>
            <p className="text-xl text-gray-600">
              Conformité aux normes CSA et ASME pour tous nos équipements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-l-4 border-l-excel-light-red">
              <CardHeader>
                <CardTitle className="text-excel-dark">Normes de sécurité</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-excel-light-red mr-2" />
                  <span>CSA B167-16</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-excel-light-red mr-2" />
                  <span>ASME B30.2</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-excel-light-red mr-2" />
                  <span>OSHA 1926.1400</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-excel-light-red">
              <CardHeader>
                <CardTitle className="text-excel-dark">Matériaux</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-excel-light-red mr-2" />
                  <span>Acier haute résistance</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-excel-light-red mr-2" />
                  <span>Finition anti-corrosion</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-excel-light-red mr-2" />
                  <span>Composants certifiés</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-excel-light-red">
              <CardHeader>
                <CardTitle className="text-excel-dark">Garanties</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-excel-light-red mr-2" />
                  <span>2 ans équipement</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-excel-light-red mr-2" />
                  <span>1 an installation</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-excel-light-red mr-2" />
                  <span>Support technique 24/7</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-excel-steel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Besoin d'une solution personnalisée ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Nos ingénieurs conçoivent des équipements sur mesure adaptés à vos contraintes spécifiques.
          </p>
          <div className="text-center mt-12">
            <Button 
              asChild 
              size="lg"
              className="bg-excel-light-red hover:bg-excel-red text-white"
            >
              <Link to="/contact">
                Obtenir un catalogue produit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;